import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, MapPin, ShieldAlert, Sparkles, CreditCard } from "lucide-react";
import { api, errMsg, fmtDate, fmtDateTime, fetchBlobUrl } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Loader, StatusBadge, PriceBreakdown, Section, Empty, Chip, DocButton } from "@/components/common";
import Chat from "@/components/Chat";
import { CodesCard, IdUpload, ComplaintDialog, ReviewBox, FileUpload } from "@/components/BookingParts";

function useAction(b, reload) {
  return async (path, body, msg) => {
    try { await api.post(`/bookings/${b.id}/${path}`, body); toast.success(msg || "OK"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
}

function PayButton({ b, reload }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [risk, setRisk] = useState(false);
  const [early, setEarly] = useState(false);
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: b.id, origin_url: window.location.origin, accept_terms: terms, accept_risk: risk, early_start: early });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <>
      <button className="w-btn w-btn-primary" onClick={() => setOpen(true)} data-testid="pay-open-btn"><CreditCard size={18} aria-hidden="true" /> {t("bk.pay")}</button>
      {open && createPortal((
        <div className="fixed inset-0 z-[70] bg-black/40 overflow-y-auto p-4 flex items-start justify-center" onClick={() => !busy && setOpen(false)} data-testid="order-confirm-dialog">
          <div className="w-card p-6 max-w-lg w-full space-y-4 my-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-extrabold text-lg">Bestellung prüfen &amp; zahlungspflichtig bestellen</h3>
            <dl className="w-card bg-gray-50 p-4 space-y-2 text-sm">
              <div className="flex justify-between gap-3"><dt className="w-muted">Vertragspartner (Wäscheleistung)</dt><dd className="font-semibold text-right">{b.provider_name} (Waschheld)</dd></div>
              <div className="flex justify-between gap-3"><dt className="w-muted">Vermittler</dt><dd className="text-right">Washly</dd></div>
              <div className="flex justify-between gap-3"><dt className="w-muted">Leistung</dt><dd className="text-right">{b.kg} kg Wäscheservice</dd></div>
              <div className="flex justify-between gap-3"><dt className="w-muted">Termin</dt><dd className="text-right">{b.date} · {b.slot} Uhr</dd></div>
              <div className="flex justify-between gap-3 border-t border-line pt-2"><dt className="font-bold">Gesamtpreis</dt><dd className="font-extrabold tabular-nums text-right" data-testid="order-total">{b.price.total.toFixed(2)} €</dd></div>
            </dl>
            <p className="w-muted text-xs">Widerrufsrecht: Für Verbraucherverträge im Fernabsatz besteht grundsätzlich ein 14-tägiges Widerrufsrecht. Vertragspartner des Widerrufs ist der jeweilige Waschheld; deine Erklärung kannst du jederzeit über „Vertrag widerrufen“ elektronisch abgeben. Einzelheiten in der <a href="/widerruf" target="_blank" rel="noreferrer" className="underline">Widerrufsbelehrung</a>.</p>
            <label className="flex items-start gap-3 text-sm cursor-pointer rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="order-risk-ack-label">
              <input type="checkbox" className="h-5 w-5 mt-0.5 accent-[var(--w-mint-ink)]" checked={risk} onChange={(e) => setRisk(e.target.checked)} data-testid="order-accept-risk" />
              <span className="leading-relaxed"><span className="flex items-center gap-1.5 font-bold text-amber-900 mb-1"><ShieldAlert size={16} aria-hidden="true" /> Wichtiger Hinweis zur Leistung &amp; Haftung</span>Ich habe verstanden, dass die Wäschedienstleistung eigenverantwortlich durch den ausgewählten Waschhelden und nicht durch Washly erbracht wird. Mir ist bewusst, dass Schäden oder Verluste trotz Sicherheitsmaßnahmen und Versicherungsprüfungen nicht vollständig ausgeschlossen werden können und Washly keine vollständige Schadensabsicherung garantiert. Meine gesetzlichen Rechte bleiben unberührt.</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="h-5 w-5 mt-0.5 accent-[var(--w-mint-ink)]" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="order-accept-terms" />
              <span>Ich habe die <a href="/agb" target="_blank" rel="noreferrer" className="underline">AGB</a> und die <a href="/widerruf" target="_blank" rel="noreferrer" className="underline">Widerrufsbelehrung</a> gelesen und akzeptiere sie.</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="h-5 w-5 mt-0.5 accent-[var(--w-mint-ink)]" checked={early} onChange={(e) => setEarly(e.target.checked)} data-testid="order-early-start" />
              <span>Ich verlange ausdrücklich, dass der Waschheld mit der Leistung bereits vor Ablauf der Widerrufsfrist beginnt. Mir ist bekannt, dass mein Widerrufsrecht bei vollständiger Leistungserbringung erlischt.</span>
            </label>
            {(!terms || !risk) && <p className="w-muted text-xs" data-testid="order-terms-hint">Bitte bestätige den Risikohinweis und die AGB, um fortzufahren.</p>}
            <button className="w-btn w-btn-primary w-full" onClick={start} disabled={busy || !terms || !risk} data-testid="order-submit-btn"><CreditCard size={18} aria-hidden="true" /> {busy ? "Weiterleitung…" : "Zahlungspflichtig bestellen"}</button>
            <p className="w-muted text-xs text-center">Sichere Zahlung über Mollie – Karte, PayPal, SEPA u. a. auf der nächsten Seite wählbar.</p>
            <button className="w-btn w-btn-secondary w-full" onClick={() => setOpen(false)} disabled={busy} data-testid="order-cancel-btn">Zurück</button>
          </div>
        </div>
      ), document.body)}
    </>
  );
}

function ProblemDialog({ b, phase, reload, onClose }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/bookings/${b.id}/handover-problem`, { phase, note }); toast.success("Problem gemeldet – Washly prüft den Fall"); onClose(); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => !busy && onClose()} data-testid="handover-problem-dialog">
      <div className="w-card p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-lg">Übergabe nicht erfolgt / Problem melden</h3>
        <textarea className="w-input !h-28 py-3" value={note} minLength={5} onChange={(e) => setNote(e.target.value)} placeholder="Was ist passiert?" data-testid="handover-problem-note" />
        <div className="flex gap-2"><button className="w-btn w-btn-secondary flex-1" onClick={onClose} disabled={busy}>Abbrechen</button>
          <button className="w-btn w-btn-primary flex-1" onClick={submit} disabled={busy || note.trim().length < 5} data-testid="handover-problem-submit">{busy ? "Sende…" : "Melden"}</button></div>
      </div>
    </div>
  );
}

function HandoverFlow({ b, reload, phase, role }) {
  const act = useAction(b, reload);
  const [showAlt, setShowAlt] = useState(false);
  const [showProblem, setShowProblem] = useState(false);
  const cur = (phase === "handover" ? b.handover_confirm : b.return_confirm) || {};
  const other = role === "provider" ? "customer" : "provider";
  const mine = cur[role], theirs = cur[other];
  const declareLabel = phase === "handover" ? "Wäsche entgegengenommen" : "Wäsche zurückgegeben";
  const confirmLabel = phase === "handover" ? "Übergabe bestätigen" : "Rückgabe bestätigen";
  const noun = phase === "handover" ? "Übergabe" : "Rückgabe";
  const pinPath = phase === "handover" ? "handover" : "return";
  if (mine && !theirs) {
    return (
      <div className="w-card p-3 bg-mint-soft space-y-2" data-testid={`${phase}-waiting-${role}`}>
        <p className="text-sm font-semibold">{role === "provider" ? "Gemeldet · Warte auf Kundenbestätigung" : "Bestätigt · Warte auf Waschheld"}</p>
        <button className="w-btn w-btn-secondary !min-h-[36px] text-sm" onClick={() => act(`remind-confirm?phase=${phase}`, {}, "Erinnerung gesendet")} data-testid={`${phase}-remind`}>Erinnerung senden</button>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={`${phase}-flow-${role}`}>
      {role === "provider" && <button className="w-btn w-btn-primary" onClick={() => act(`${phase}/declare`, {}, `${noun} gemeldet`)} data-testid={`${phase}-declare-btn`}>{declareLabel}</button>}
      {role === "customer" && (theirs ? (
        <>
          <p className="w-muted text-xs">Bitte bestätige nur, wenn die Wäsche tatsächlich übergeben wurde. Bei einer Familienbuchung kannst du dies vorher telefonisch mit dem Empfänger klären.</p>
          <button className="w-btn w-btn-primary" onClick={() => act(`${phase}/confirm`, {}, `${noun} bestätigt`)} data-testid={`${phase}-confirm-btn`}>{confirmLabel}</button>
        </>
      ) : <p className="w-card p-3 bg-gray-50 text-sm" data-testid={`${phase}-awaiting-provider`}>Warte auf Waschheld – die {noun} wurde noch nicht gemeldet.</p>)}
      {role === "provider" && (
        <div>
          <button type="button" className="text-sm underline w-muted" onClick={() => setShowAlt(!showAlt)} data-testid={`${phase}-alt-toggle`}>Alternativ per QR-Code / PIN bestätigen</button>
          {showAlt && <div className="mt-2"><PinForm b={b} path={pinPath} label={confirmLabel} reload={reload} /></div>}
        </div>
      )}
      <button type="button" className="text-sm underline text-[var(--w-error)] block" onClick={() => setShowProblem(true)} data-testid={`${phase}-problem-open`}>Übergabe nicht erfolgt / Problem melden</button>
      {showProblem && <ProblemDialog b={b} phase={phase} reload={reload} onClose={() => setShowProblem(false)} />}
    </div>
  );
}

function CustomerActions({ b, reload }) {
  const { t } = useT();
  const act = useAction(b, reload);
  return (
    <div className="flex flex-col gap-3" data-testid="customer-actions">
      {b.status === "accepted" && <PayButton b={b} reload={reload} />}
      {["requested", "accepted"].includes(b.status) && <button className="w-btn w-btn-secondary self-start" onClick={() => act("cancel", {}, "Storniert")} data-testid="cancel-btn">{t("bk.cancel")}</button>}
      {b.status === "paid" && <HandoverFlow b={b} reload={reload} phase="handover" role="customer" />}
      {["washing", "ready"].includes(b.status) && <HandoverFlow b={b} reload={reload} phase="return" role="customer" />}
      {b.status === "returned" && <button className="w-btn w-btn-primary self-start" onClick={() => act("confirm", {}, "Abgeschlossen – danke!")} data-testid="confirm-btn">{t("bk.confirm")}</button>}
      {b.status === "returned" && <p className="w-muted text-xs">Die Rückgabebestätigung ist keine Bestätigung, dass die Wäsche mängelfrei ist. Prüfe deine Wäsche und bestätige „Alles in Ordnung" oder melde ein Problem.</p>}
      {["paid", "picked_up", "washing", "ready", "returned", "completed"].includes(b.status) && !b.complaint_open && <ComplaintDialog b={b} reload={reload} />}
      {["accepted", "paid", "picked_up", "washing", "ready", "returned"].includes(b.status) && (
        <Link to={`/widerruf-erklaeren?booking=${b.code}`} className="inline-flex items-center text-sm w-muted hover:text-ink underline" data-testid="booking-withdraw-link">Vertrag widerrufen</Link>
      )}
    </div>
  );
}

function PinForm({ b, path, label, reload }) {
  const { t } = useT();
  const [pin, setPin] = useState("");
  const act = useAction(b, reload);
  return (
    <form className="flex flex-wrap gap-2 items-end" onSubmit={(e) => { e.preventDefault(); act(path, { pin }, label); }}>
      <label><span className="w-label">{t("bk.enter_pin")}</span>
        <input className="w-input !w-44 text-xl tracking-[0.25em] font-bold" inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid={`${path}-pin-input`} /></label>
      <button className="w-btn w-btn-primary" disabled={pin.length !== 6} data-testid={`${path}-submit-btn`}>{label}</button>
    </form>
  );
}

function Photo({ fileId, label }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    fetchBlobUrl(fileId).then((x) => { u = x; setUrl(x); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [fileId]);
  return <figure><figcaption className="w-label">{label}</figcaption>{url ? <img src={url} alt={label} className="rounded-xl h-40 w-full object-cover" /> : <div className="h-40 rounded-xl bg-gray-100" />}</figure>;
}

function Photos({ b, reload, canUpload }) {
  const { t } = useT();
  const up = (kind) => async (f) => {
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", f);
    try { await api.post(`/bookings/${b.id}/photos`, fd); toast.success("Foto gespeichert"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const pc = b.photo_check;
  return (
    <Section title={t("bk.photos")} testId="photos-section">
      <div className="grid grid-cols-2 gap-4">
        {["before", "after"].map((k) => (
          <div key={k} className="space-y-2">
            {b.photos?.[k] ? <Photo fileId={b.photos[k]} label={t(`bk.${k}`)} /> : <p className="w-label">{t(`bk.${k}`)}: –</p>}
            {canUpload && <FileUpload label={t(`bk.${k}`)} onFile={up(k)} testId={`photo-upload-${k}`} accept="image/jpeg,image/png,image/webp" />}
          </div>
        ))}
      </div>
      {pc && (
        <div className="mt-4 w-card p-4" data-testid="photo-check-result">
          <p className="font-bold flex items-center gap-2"><Sparkles size={18} aria-hidden="true" /> {t("bk.photo_check")}</p>
          <p className="mt-1">{pc.plausible === true ? <Chip tone="mint">✓ plausibel</Chip> : pc.plausible === false ? <Chip tone="error">! auffällig</Chip> : <Chip>nicht geprüft</Chip>}
            {pc.damage_suspected && <Chip tone="warn">Schaden vermutet</Chip>}</p>
          <p className="w-muted mt-1">{pc.notes}</p>
        </div>
      )}
    </Section>
  );
}

function HeavySoilBanner({ b }) {
  if (!b.heavy_soil) return null;
  return (
    <div className="w-card p-4 bg-[var(--w-warn-soft)] border-amber-300 space-y-1" data-testid="heavy-soil-banner">
      <p className="font-bold flex items-center gap-2"><AlertTriangle size={18} aria-hidden="true" /> Stark verunreinigte Wäsche</p>
      <p className="text-sm">Angabe des Kunden: „{b.heavy_soil_desc}"</p>
      <p className="w-muted text-sm">Hygienezuschlag ist im Gesamtpreis enthalten. Bitte getrennt verpackt übergeben.</p>
    </div>
  );
}

const DTYPE_LABELS = { seide: "Seide", kaschmir: "Kaschmir", wolle: "Wolle", andere: "Andere empfindliche Textilien" };

function DelicateBanner({ b }) {
  if (!b.delicate) return null;
  return (
    <div className="w-card p-4 bg-mint-soft border-mint space-y-1" data-testid="delicate-banner">
      <p className="font-bold flex items-center gap-2"><ShieldAlert size={18} aria-hidden="true" /> Empfindliche Textilien enthalten</p>
      <p className="text-sm">Arten: {(b.delicate_types || []).map((k) => DTYPE_LABELS[k] || k).join(", ") || "–"}</p>
      {b.delicate_notes && <p className="text-sm">Pflegehinweise: {b.delicate_notes}</p>}
      {(b.delicate_label_photos || []).length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="delicate-label-photos">
          {b.delicate_label_photos.map((fid, i) => <DocButton key={fid} fileId={fid} label={`Etikett ${i + 1}`} testId={`delicate-label-${i}`} />)}
        </div>
      )}
      <p className="w-muted text-sm">Pflegeetiketten beachten. Keine ungeeigneten Waschverfahren zusagen; nicht geeignete Textilien bitte ablehnen.</p>
    </div>
  );
}

function AcceptBlock({ b, act }) {
  const { t } = useT();
  const [hs, setHs] = useState(false);
  const [dc, setDc] = useState(false);
  const needHs = b.heavy_soil, needDc = b.delicate;
  const ready = (!needHs || hs) && (!needDc || dc);
  return (
    <>
      {b.conflict_warning && <p role="alert" className="w-card p-3 bg-[var(--w-warn-soft)] text-[var(--w-warn)] font-semibold" data-testid="provider-conflict-warning"><AlertTriangle size={16} className="inline mr-1" aria-hidden="true" />{t("bk.conflict")}</p>}
      {needHs && (
        <label className="flex items-start gap-2 text-sm cursor-pointer w-card p-3" data-testid="heavy-soil-consent">
          <input type="checkbox" className="h-5 w-5 mt-0.5 accent-[var(--w-mint-ink)]" checked={hs} onChange={(e) => setHs(e.target.checked)} data-testid="heavy-soil-consent-check" />
          <span>Ich nehme die stark verunreinigte Wäsche ausdrücklich an und kann sie hygienisch sicher bearbeiten.</span>
        </label>
      )}
      {needDc && (
        <label className="flex items-start gap-2 text-sm cursor-pointer w-card p-3" data-testid="delicate-consent">
          <input type="checkbox" className="h-5 w-5 mt-0.5 accent-[var(--w-mint-ink)]" checked={dc} onChange={(e) => setDc(e.target.checked)} data-testid="delicate-consent-check" />
          <span>Ich kann die angegebenen empfindlichen Textilien fachgerecht und gemäß Pflegeetikett behandeln. Andernfalls lehne ich den Auftrag ab.</span>
        </label>
      )}
      <div className="flex gap-3">
        <button className="w-btn w-btn-primary" disabled={!ready} onClick={() => act("accept", { heavy_soil_confirmed: !!needHs, delicate_confirmed: !!needDc }, "Angenommen")} data-testid="accept-btn">{t("bk.accept")}</button>
        <button className="w-btn w-btn-secondary" onClick={() => act("decline", {}, "Abgelehnt")} data-testid="decline-btn">{t("bk.decline")}</button>
      </div>
    </>
  );
}

function HygieneHoldButton({ b, reload, kind = "hygiene" }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const label = kind === "delicate" ? "Nicht angegebene empfindliche Textilien – Klärung anfordern" : "Hygienische Bedenken melden / Bearbeitung stoppen";
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/bookings/${b.id}/hygiene-hold`, { reason, kind }); toast.success("Bearbeitung gestoppt – gemeldet"); setOpen(false); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button className="w-btn w-btn-secondary !text-[var(--w-error)]" onClick={() => setOpen(true)} data-testid={`hold-btn-${kind}`}><ShieldAlert size={16} aria-hidden="true" /> {label}</button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => !busy && setOpen(false)} data-testid="hygiene-hold-dialog">
          <div className="w-card p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg">Bearbeitung stoppen – {kind === "delicate" ? "empfindliche Textilien" : "hygienische Gründe"}</h3>
            <p className="w-muted text-sm">{kind === "delicate" ? "Nutze dies, wenn du nicht angegebene empfindliche Textilien entdeckst. Die Bearbeitung wird gestoppt und mit dem Kunden geklärt." : "Nutze dies, wenn die tatsächliche Verunreinigung erheblich abweicht oder eine Gesundheitsgefährdung nicht ausgeschlossen werden kann."} Die Wäsche wird bis zur Klärung nicht bearbeitet.</p>
            <label className="block"><span className="w-label">Grund (Pflicht)</span>
              <textarea className="w-input !h-28 py-3" value={reason} minLength={10} onChange={(e) => setReason(e.target.value)} data-testid="hygiene-hold-reason" /></label>
            <div className="flex gap-2"><button className="w-btn w-btn-secondary flex-1" onClick={() => setOpen(false)} disabled={busy}>Abbrechen</button>
              <button className="w-btn w-btn-primary flex-1" onClick={submit} disabled={busy || reason.trim().length < 10} data-testid="hygiene-hold-submit">{busy ? "Sende…" : "Bearbeitung stoppen"}</button></div>
          </div>
        </div>
      )}
    </>
  );
}

function ProviderActions({ b, reload }) {
  const { t } = useT();
  const act = useAction(b, reload);
  const canHold = ["paid", "picked_up", "washing", "ready"].includes(b.status);
  return (
    <div className="space-y-4" data-testid="provider-actions">
      <HeavySoilBanner b={b} />
      <DelicateBanner b={b} />
      {b.status === "requested" && <AcceptBlock b={b} act={act} />}
      {b.status === "accepted" && <p className="w-muted">Warte auf Zahlung des Kunden.</p>}
      {b.status === "paid" && (
        <>
          {b.self_pickup && <p className={`font-semibold ${b.id_doc_file_id ? "text-mint-ink" : "text-[var(--w-warn)]"}`} data-testid="provider-id-status">{b.id_doc_file_id ? "✓ Ausweis des Kunden hinterlegt" : "! Ausweis des Kunden fehlt noch (Selbstabholung)"}</p>}
          <HandoverFlow b={b} reload={reload} phase="handover" role="provider" />
        </>
      )}
      {b.status === "picked_up" && <button className="w-btn w-btn-primary" onClick={() => act("progress", { status: "washing" }, "Status aktualisiert")} data-testid="progress-washing-btn">{t("bk.start")}</button>}
      {b.status === "washing" && <button className="w-btn w-btn-primary" onClick={() => act("progress", { status: "ready" }, "Als fertig markiert")} data-testid="progress-ready-btn">{t("bk.ready")}</button>}
      {["washing", "ready"].includes(b.status) && <HandoverFlow b={b} reload={reload} phase="return" role="provider" />}
      {b.status === "hygiene_hold" && (
        <div className="space-y-3" data-testid="hygiene-hold-active">
          <p className="w-card p-3 bg-[var(--w-error-soft)] text-sm">Bearbeitung gestoppt (Hygieneprüfung). Grund: „{b.hygiene_hold?.reason}". Bitte organisiere die sichere Rückgabe der unbearbeiteten Wäsche und bestätige sie mit dem Rückgabe-Code des Kunden.</p>
          <FileUpload label="Beweisfoto hochladen (optional)" onFile={async (file) => { const fd = new FormData(); fd.append("kind", "hygiene"); fd.append("file", file); try { await api.post(`/bookings/${b.id}/photos`, fd); toast.success("Foto gespeichert"); reload(); } catch (e) { toast.error(errMsg(e)); } }} testId="hygiene-photo-upload" accept="image/jpeg,image/png,image/webp" />
          <PinForm b={b} path="return-unprocessed" label="Unbearbeitete Rückgabe bestätigen" reload={reload} />
        </div>
      )}
      {b.status === "returned_unprocessed" && <p className="w-muted">Unbearbeitete Wäsche zurückgegeben. Eine etwaige Erstattung prüft Washly.</p>}
      {canHold && <HygieneHoldButton b={b} reload={reload} kind="hygiene" />}
      {canHold && b.delicate && <HygieneHoldButton b={b} reload={reload} kind="delicate" />}
    </div>
  );
}

function Address({ title, a }) {
  if (!a) return null;
  return <div className="w-card p-4"><p className="w-label flex items-center gap-1"><MapPin size={14} aria-hidden="true" /> {title}</p><p className="font-semibold">{a.street}</p><p>{a.plz} {a.city}</p>{a.notes && <p className="w-muted">{a.notes}</p>}{a.phone && <p className="w-muted">{a.phone}</p>}</div>;
}

export default function BookingDetail() {
  const { id } = useParams();
  const { t, lang } = useT();
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  const reload = useCallback(() => { api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch((e) => setError(errMsg(e))); }, [id]);
  useEffect(() => { reload(); }, [reload]);

  if (error) return <div className="w-container py-10"><Empty>{error}</Empty></div>;
  if (!b) return <Loader />;
  const role = b.viewer_role;
  return (
    <div className="w-container py-8 grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-7 space-y-6">
        <div className="w-card p-6 space-y-4 w-rise">
          <div className="flex flex-wrap justify-between gap-3 items-start">
            <div>
              <p className="w-muted">{b.code}</p>
              <h1 className="text-2xl font-extrabold" data-testid="booking-detail-title">{role === "provider" ? b.customer_name : b.provider_name}</h1>
              <p className="w-muted">{fmtDate(b.date, lang)} · {b.slot} Uhr · {b.kg} kg{b.self_pickup ? " · Selbstabholung" : ""}</p>
            </div>
            <StatusBadge status={b.status} testId="booking-status" />
          </div>
          {role !== "provider" && <HeavySoilBanner b={b} />}
          {role !== "provider" && <DelicateBanner b={b} />}
          {role === "customer" && <CustomerActions b={b} reload={reload} />}
          {role === "provider" && <ProviderActions b={b} reload={reload} />}
          {role !== "provider" && b.status === "hygiene_hold" && (
            <div className="w-card p-4 bg-[var(--w-error-soft)]" data-testid="hygiene-hold-info">
              <p className="font-bold text-[var(--w-error)] flex items-center gap-2"><ShieldAlert size={18} aria-hidden="true" /> Bearbeitung gestoppt (Hygieneprüfung)</p>
              <p className="mt-1 text-sm">Der Waschheld hat die Bearbeitung aus hygienischen Gründen gestoppt. Deine Wäsche wird nicht bearbeitet; wir organisieren die sichere Rückgabe.{b.hygiene_hold?.reason && role === "staff" ? ` Grund: ${b.hygiene_hold.reason}` : ""}</p>
            </div>
          )}
          {role === "customer" && b.self_pickup && ["requested", "accepted", "paid"].includes(b.status) && <IdUpload b={b} reload={reload} />}
          {b.complaint && (
            <div className="w-card p-4 bg-[var(--w-error-soft)]" data-testid="complaint-info">
              <p className="font-bold text-[var(--w-error)] flex items-center gap-2"><ShieldAlert size={18} aria-hidden="true" /> Reklamation: {b.complaint.type} – {b.complaint.status === "open" ? "in Prüfung" : `entschieden (${b.complaint.decision})`}</p>
              <p className="mt-1">{b.complaint.description}</p>
            </div>
          )}
        </div>
        {role === "customer" && ["accepted", "paid", "picked_up", "washing", "ready"].includes(b.status) && <CodesCard b={b} />}
        {["paid", "picked_up", "washing", "ready", "returned", "completed", "disputed"].includes(b.status) && (role !== "customer" || b.photos?.before) && <Photos b={b} reload={reload} canUpload={role === "provider" && b.status !== "completed"} />}
        <Chat bookingId={b.id} readOnly={b.chat_read_only} />
        {b.status === "completed" && role !== "staff" && <ReviewBox b={b} reload={reload} />}
      </div>
      <aside className="lg:col-span-5 space-y-6">
        <Section title="Preis" testId="booking-price"><PriceBreakdown quote={b.price} testId="detail-price" />
          {b.refunded_amount > 0 && <p className="mt-2 font-semibold text-[var(--w-warn)]" data-testid="refunded-amount">Erstattet: {b.refunded_amount.toFixed(2)} €</p>}
          {b.payment && <p className="w-muted mt-2">Zahlung: {b.payment.psp} · {b.payment.psp_ref}</p>}</Section>
        <Address title={t("bk.customer_address")} a={b.address} />
        <Address title={t("bk.provider_address")} a={b.provider_address} />
        {role === "customer" && !b.provider_address && <p className="w-muted">Die Adresse des Anbieters wird nach Bestätigung sichtbar.</p>}
        <Section title={t("bk.timeline")} testId="booking-timeline">
          <ol className="space-y-3 border-l-2 border-line pl-4">
            {(b.timeline || []).map((e, i) => (
              <li key={i}><StatusBadge status={e.status} testId={`timeline-${i}`} /><p className="w-muted text-sm mt-1">{fmtDateTime(e.at, lang)} · {e.by}{e.note ? ` · ${e.note}` : ""}</p></li>
            ))}
          </ol>
        </Section>
        {role === "staff" && (
          <Section title="Übergabe-Protokoll" testId="handover-log">
            {[["handover", "Schmutzwäsche-Übergabe", b.handover_confirm], ["return", "Rückgabe", b.return_confirm]].map(([k, lbl, c]) => (
              <div key={k} className="mb-3 text-sm" data-testid={`handover-log-${k}`}>
                <p className="font-semibold">{lbl}</p>
                <p className="w-muted">Waschheld: {c?.provider ? `${fmtDateTime(c.provider.at, lang)} (${c.provider.method})` : "offen"}</p>
                <p className="w-muted">Kunde: {c?.customer ? `${fmtDateTime(c.customer.at, lang)} (${c.customer.method})` : "offen"}</p>
              </div>
            ))}
            {(b.handover_problems || []).length > 0 && (
              <div className="mt-2 text-sm" data-testid="handover-log-problems">
                <p className="font-semibold text-[var(--w-error)]">Gemeldete Probleme</p>
                {b.handover_problems.map((p, i) => <p key={i} className="w-muted">{fmtDateTime(p.at, lang)} · {p.role} · {p.phase}: {p.note}</p>)}
              </div>
            )}
          </Section>
        )}
      </aside>
    </div>
  );
}
